import React, { useEffect, useState } from "react";
import { Box, Flex, Spinner } from "theme-ui";
import moment from "moment-timezone";

import H5 from "components/typography/H5";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { mnt, mntCard } from "theme/maintenanceTokens";
import { useToast } from "components/toast";

import { getPlanRecurringWorkRuns, runPlanRecurringWork } from "api/ServicePlans";

/**
 * design/CADENCE_SCHEDULER.md §8.1 — the Plan Detail "Recurring Work"
 * right-rail panel. Reads the wo_cadence_run audit history for this plan
 * and, for admins, offers a "Run now" that triggers a manual generation
 * pass (runReason='manual').
 *
 * "Recurring Work" is the user-facing name; the endpoints stay
 * /maintenance/cadence/* (code debt, matching the module's cadence*
 * identifiers).
 *
 * Render gate lives in the PARENT — this panel only mounts when the plan
 * has at least one product row carrying a live cadenceRule. That keeps
 * it off the rail for plans that don't use recurring generation. It
 * still handles an empty run history (rule configured, scheduler hasn't
 * fired yet) with a clear "no runs yet" state.
 *
 * A run row summarizes one firing: when, why (nightly / manual / catch-
 * up), how many WOs it generated, and how many it skipped as duplicates.
 * The errorSummary (overdue notes + per-site errors) is surfaced as a
 * count with the detail in a title tooltip rather than a wall of text —
 * the audit row is the source of truth if someone needs the full JSON.
 */
export default function RecurringWorkPanel({
  planId,
  canRun,
}: {
  planId: string;
  canRun: boolean;
}) {
  const toast = useToast();
  const [runs, setRuns] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await getPlanRecurringWorkRuns({ data: { id: planId, limit: 10 } });
    setRuns(Array.isArray(res) ? res : []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [planId]);

  const onRunNow = async () => {
    setRunning(true);
    const res = await runPlanRecurringWork({ data: { id: planId } });
    setRunning(false);
    // The controller returns { success, run }. A run with skipped set
    // means nothing was eligible; otherwise report what it generated.
    const run = res?.run;
    if (res?.success && run) {
      if (run.skipped === "no_rules") {
        toast.add("No recurring schedule is configured on this plan.", "var(--theme-ui-colors-info)");
      } else if (run.skipped === "not_open") {
        toast.add("This plan is not open, so nothing was generated.", "var(--theme-ui-colors-info)");
      } else {
        const g = run.wosGenerated ?? 0;
        toast.add(
          `Recurring Work ran: ${g} work order${g === 1 ? "" : "s"} generated, ${run.wosSkippedDup ?? 0} skipped.`,
          "var(--theme-ui-colors-success)",
        );
      }
      await load();
    } else {
      toast.add(res?.message || "Could not run Recurring Work.", "var(--theme-ui-colors-danger)");
    }
  };

  const latest = runs && runs.length ? runs[0] : null;

  return (
    <Box>
      <Flex sx={{ alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
        <H5 fontFamily="Poppins" fontWeight={600} fontSize={"14px"} margin={0}>
          Recurring Work
        </H5>
        {canRun && (
          <Box
            as="button"
            onClick={onRunNow}
            {...({ disabled: running } as any)}
            sx={{
              background: "transparent",
              border: "none",
              padding: 0,
              fontSize: "12px",
              fontFamily: "Poppins",
              color: running ? mnt.textFaint : mnt.link,
              cursor: running ? "default" : "pointer",
              "&:hover": { textDecoration: running ? "none" : "underline" },
            }}
          >
            {running ? "Running…" : "Run now"}
          </Box>
        )}
      </Flex>

      <Box
        sx={{
          ...mntCard,
          padding: "14px 16px",
        }}
      >
        {loading ? (
          <Box sx={{ textAlign: "center", paddingY: "12px" }}>
            <Spinner size={20} />
          </Box>
        ) : !runs || runs.length === 0 ? (
          <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
            No runs yet. The schedule is set; the nightly job will create work orders when sites come due, or use &quot;Run now&quot; to generate them immediately.
          </Subtext>
        ) : (
          <>
            {/* Latest-run headline */}
            <Flex sx={{ justifyContent: "space-between", alignItems: "baseline" }}>
              <Span fontFamily={"Poppins"} fontSize={"12px"} fontWeight={600}>
                Last run
              </Span>
              <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
                {moment(latest.runAt).fromNow()}
              </Span>
            </Flex>
            <Subtext
              fontSize={"12px"}
              fontFamily={"Poppins"}
              margin={0}
              marginTop={"4px"}
            >
              {summarizeRun(latest)}
            </Subtext>

            {/* Prior runs, compact */}
            {runs.length > 1 && (
              <Box
                sx={{
                  marginTop: "12px",
                  borderTop: "0.5px solid " + mnt.divider,
                  paddingTop: "10px",
                }}
              >
                <Subtext
                  fontSize={"11px"}
                  fontFamily={"Poppins"}
                  fontWeight={600}
                  margin={0}
                  marginBottom={"6px"}
                  color={mnt.textMuted}
                >
                  EARLIER RUNS
                </Subtext>
                {runs.slice(1).map((r) => (
                  <Flex
                    key={r.id}
                    sx={{ justifyContent: "space-between", marginTop: "5px", alignItems: "baseline" }}
                    title={r.errorSummary || undefined}
                  >
                    <Span fontFamily={"Poppins"} fontSize={"11px"} color={mnt.textMuted}>
                      {moment(r.runAt).format("MMM D")} · {humanReason(r.runReason)}
                    </Span>
                    <Span fontFamily={"Poppins"} fontSize={"11px"}>
                      {r.wosGenerated} gen · {r.wosSkippedDup} skip
                    </Span>
                  </Flex>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

function humanReason(reason: string): string {
  if (reason === "manual") return "manual";
  if (reason === "catchup") return "catch-up";
  return "nightly";
}

function summarizeRun(run: any): string {
  const gen = run.wosGenerated ?? 0;
  const dup = run.wosSkippedDup ?? 0;
  const evaluated = run.sitesEvaluated ?? 0;
  const errCount = errorCount(run.errorSummary);
  const parts = [
    `${gen} work order${gen === 1 ? "" : "s"} generated`,
    `${dup} skipped as duplicates`,
    `${evaluated} checked`,
  ];
  let s = parts.join(", ") + ".";
  if (errCount > 0) {
    s += ` ${errCount} note${errCount === 1 ? "" : "s"} (hover earlier runs for detail).`;
  }
  return s;
}

/** errorSummary is a JSON array of { lcpId, error } or null. Count the
 * entries defensively — it may already be a parsed array or a string. */
function errorCount(errorSummary: any): number {
  if (!errorSummary) return 0;
  if (Array.isArray(errorSummary)) return errorSummary.length;
  try {
    const parsed = JSON.parse(errorSummary);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}
